import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { SiteNavDropdownColumn } from './SiteNavDropdownColumn.tsx';
import { SiteNavDropdownImageModule } from './SiteNavDropdownImageModule.tsx';
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SiteNav-style.scss';

import { type SiteNavDropdownContent_navigationModule$key } from './__generated__/SiteNavDropdownContent_navigationModule.graphql.ts';

type Props = {
    trackingLabelPrefix?: string;
    navigationModule: SiteNavDropdownContent_navigationModule$key;
    isVisible: boolean;
};

export const SiteNavDropdownContent: FC<Props> = ({
    navigationModule: navigationModuleRef,
    trackingLabelPrefix,
    isVisible,
}) => {
    const navigationModule = useFragment(
        graphql`
            fragment SiteNavDropdownContent_navigationModule on NavigationModule {
                name
                items {
                    name
                    column
                    items {
                        name
                    }
                    ...SiteNavDropdownColumn_navigationTopLevel
                }

                imageUrl
                ...SiteNavDropdownImageModule_navigationModule
            }
        `,
        navigationModuleRef
    );

    const { imageUrl, items } = navigationModule;
    const columns: Record<string, NonNullable<typeof items>[number][]> = {};
    let imageModule;

    // Group columns by column number
    items?.forEach(item => {
        if (item?.column) {
            if (!columns[item.column]) {
                columns[item.column] = [];
            }
            columns[item.column].push(item);
        }
    });

    if (imageUrl) {
        imageModule = (
            <div
                className={classnames(
                    styles.imageModule,
                    styles.dropdownCol,
                    dibsCss.mlSmall,
                    dibsCss.w20,
                    dibsCss.ptMedium,
                    dibsCss.px0,
                    dibsCss.pbLarger
                )}
            >
                <SiteNavDropdownImageModule
                    navigationModuleRef={navigationModule}
                    isVisible={isVisible}
                />
            </div>
        );
    }

    return (
        <div
            className={classnames(
                styles.dropdownContent,
                dibsCss.flex,
                dibsCss.my0,
                dibsCss.mxAuto
            )}
        >
            {Object.values(columns).map(
                itemCol =>
                    itemCol && (
                        <SiteNavDropdownColumn
                            key={itemCol.map(item => item?.name).join()}
                            navigationItems={itemCol.filter(
                                (item): item is NonNullable<typeof item> => item !== null
                            )}
                            trackingLabelPrefix={trackingLabelPrefix || ''}
                        />
                    )
            )}
            {imageModule}
        </div>
    );
};
